import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { 
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, 
  RefreshCw, AlertTriangle, Lightbulb, Trash2, ChevronDown,
  CalendarRange, Copy, Layers
} from 'lucide-react';
import { 
  format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, isSameDay, 
  startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval, isSameMonth,
  parseISO
} from 'date-fns';
import { toast } from 'sonner';
import BulkPhysicianDialog from '../../components/schedules/BulkPhysicianDialog';

// Helper function to parse date strings consistently without timezone shift
// This ensures "2026-03-07" is treated as local date, not UTC
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  // Parse as local date by splitting and creating explicitly
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};
import CopyDuplicateDialog from '../../components/schedules/CopyDuplicateDialog';

const HOURS = Array.from({ length: 15 }, (_, i) => i + 7); // 7 AM to 9 PM

export default function PhysicianSchedule({ embedded = false }) {
  const { api, isAdmin } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');
  const [schedules, setSchedules] = useState([]);
  const [physicians, setPhysicians] = useState([]);
  const [fairnessStats, setFairnessStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [singleDayDialogOpen, setSingleDayDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [copyDuplicateDialogOpen, setCopyDuplicateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [conflictWarning, setConflictWarning] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '17:00',
    event_type: 'PHYSICIAN_CUSTOM',
    assigned_to: '',
    notes: ''
  });

  const dateRange = useMemo(() => {
    if (viewMode === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }),
        end: endOfWeek(currentDate, { weekStartsOn: 1 })
      };
    } else if (viewMode === 'month') {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
      return { start: calendarStart, end: calendarEnd };
    } else {
      return { start: currentDate, end: currentDate };
    }
  }, [currentDate, viewMode]);

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const schedulesRes = await api.get('/schedules', {
        params: {
          start_date: format(dateRange.start, 'yyyy-MM-dd'),
          end_date: format(dateRange.end, 'yyyy-MM-dd'),
          schedule_type: 'PHYSICIAN'
        }
      });
      setSchedules(schedulesRes.data);

      if (isAdmin) {
        try {
          const usersRes = await api.get('/users?status=active');
          setPhysicians(usersRes.data.filter(u => u.role === 'physician'));
          
          const statsRes = await api.get('/schedules/physician-stats');
          setFairnessStats(statsRes.data);
        } catch (adminError) {
          console.log('Admin data fetch failed:', adminError);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch schedules');
    } finally {
      setLoading(false);
    }
  };

  const navigate = (direction) => {
    if (viewMode === 'week') {
      setCurrentDate(direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
    } else if (viewMode === 'month') {
      setCurrentDate(direction === 'next' ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    } else {
      setCurrentDate(direction === 'next' ? addDays(currentDate, 1) : addDays(currentDate, -1));
    }
  };

  const getSchedulesForDate = (date) => {
    return schedules.filter(s => {
      const scheduleDate = parseLocalDate(s.date);
      return isSameDay(scheduleDate, date);
    });
  };

  const getPhysicianName = (id) => {
    const physician = physicians.find(p => p.id === id);
    if (physician) return `Dr. ${physician.last_name}`;
    const schedule = schedules.find(s => s.assigned_to === id);
    return schedule?.title?.split(' - ')[0] || 'Physician';
  };

  const getEventColor = (eventType) => {
    switch (eventType) {
      case 'PHYSICIAN_STANDARD':
      case 'PHYSICIAN_CUSTOM':
        return 'bg-blue-50 border-l-4 border-blue-500 text-blue-900';
      case 'PHYSICIAN_AFTERHOURS_WEEKDAY':
      case 'PHYSICIAN_AFTERHOURS_SATURDAY':
        return 'bg-amber-50 border-l-4 border-amber-500 text-amber-900';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const checkConflicts = (physicianId, date, excludeScheduleId = null) => {
    const dateToCheck = typeof date === 'string' ? parseLocalDate(date) : date;
    return schedules.filter(s => 
      s.assigned_to === physicianId && 
      isSameDay(parseLocalDate(s.date), dateToCheck) &&
      s.id !== excludeScheduleId
    );
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    const scheduleDate = formData.date;
    const conflicts = checkConflicts(formData.assigned_to, scheduleDate);
    if (conflicts.length > 0) {
      setConflictWarning(`This physician already has ${conflicts.length} schedule(s) on this day.`);
    }

    try {
      const physician = physicians.find(p => p.id === formData.assigned_to);
      const eventLabel = formData.event_type === 'PHYSICIAN_CUSTOM' 
        ? 'Custom' 
        : formData.event_type.replace('PHYSICIAN_', '').replace(/_/g, ' ');
      await api.post('/schedules', {
        ...formData,
        title: `Dr. ${physician?.last_name || 'Physician'} - ${eventLabel}`,
        date: scheduleDate
      });
      toast.success('Schedule created successfully');
      setSingleDayDialogOpen(false);
      setConflictWarning(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateSchedule = async (e) => {
    e.preventDefault();
    if (!selectedSchedule) return;
    setFormLoading(true);

    try {
      const physician = physicians.find(p => p.id === formData.assigned_to);
      await api.patch(`/schedules/${selectedSchedule.id}`, {
        title: `Dr. ${physician?.last_name || 'Physician'} - ${formData.event_type.replace('PHYSICIAN_', '').replace(/_/g, ' ')}`,
        start_time: formData.start_time,
        end_time: formData.end_time,
        assigned_to: formData.assigned_to,
        notes: formData.notes
      });
      toast.success('Schedule updated successfully');
      setEditDialogOpen(false);
      setConflictWarning(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update schedule');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteSchedule = async () => {
    if (!selectedSchedule) return;
    setFormLoading(true);

    try {
      await api.delete(`/schedules/${selectedSchedule.id}`);
      toast.success('Schedule deleted');
      setDeleteDialogOpen(false);
      setSelectedSchedule(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    } finally {
      setFormLoading(false);
    }
  };

  const openSingleDayDialog = (date) => {
    setSelectedDate(date);
    setConflictWarning(null);
    setFormData({
      title: '',
      date: format(date, 'yyyy-MM-dd'),
      start_time: '09:00',
      end_time: '17:00',
      event_type: 'PHYSICIAN_CUSTOM',
      assigned_to: fairnessStats?.suggested_next || physicians[0]?.id || '',
      notes: ''
    });
    setSingleDayDialogOpen(true);
  };

  const openEditDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setConflictWarning(null);
    setFormData({
      title: schedule.title,
      date: schedule.date,
      start_time: schedule.start_time,
      end_time: schedule.end_time,
      event_type: schedule.event_type,
      assigned_to: schedule.assigned_to,
      notes: schedule.notes || ''
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setDeleteDialogOpen(true);
  };

  const handleEventTypeChange = (eventType) => {
    let startTime = '09:00';
    let endTime = '17:00';

    // After-hours patterns with correct times
    if (eventType === 'PHYSICIAN_AFTERHOURS_WEEKDAY') {
      startTime = '17:00';
      endTime = '19:30';
    } else if (eventType === 'PHYSICIAN_AFTERHOURS_SATURDAY') {
      startTime = '08:30';
      endTime = '12:30';
    }

    setFormData({ ...formData, event_type: eventType, start_time: startTime, end_time: endTime });
  };

  const weekDays = useMemo(() => {
    if (viewMode !== 'week') return [];
    return Array.from({ length: 7 }, (_, i) => addDays(dateRange.start, i));
  }, [dateRange, viewMode]);

  const monthDays = useMemo(() => {
    if (viewMode !== 'month') return [];
    return eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
  }, [dateRange, viewMode]);

  const renderScheduleEvent = (schedule, compact = false) => (
    <div
      key={schedule.id}
      className={`p-2 rounded mb-1 ${getEventColor(schedule.event_type)} group relative cursor-pointer`}
      onClick={(e) => {
        e.stopPropagation();
        if (isAdmin) openEditDialog(schedule);
      }}
      data-testid={`schedule-event-${schedule.id}`}
    >
      <p className={`font-medium ${compact ? 'text-xs' : 'text-sm'} truncate`}>
        {getPhysicianName(schedule.assigned_to)}
      </p>
      {!compact && (
        <p className="text-xs opacity-75">{schedule.start_time} - {schedule.end_time}</p>
      )}
      {isAdmin && (
        <div className="absolute top-1 right-1 hidden group-hover:flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 bg-white/80 hover:bg-white"
            onClick={(e) => {
              e.stopPropagation();
              openDeleteDialog(schedule);
            }}
          >
            <Trash2 className="h-3 w-3 text-red-500" />
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className={`space-y-6 ${embedded ? '' : ''}`} data-testid="physician-schedule-page">
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Physician Schedule</h1>
            <p className="text-slate-500 mt-1">Manage physician appointments and after-hours coverage</p>
          </div>
        </div>
      )}

      {/* Admin Action Buttons - CLEAR AND SEPARATED */}
      {isAdmin && (
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-slate-700 mr-2">Schedule Actions:</span>
              
              {/* Primary: Bulk Add */}
              <Button 
                className="bg-teal-600 hover:bg-teal-700"
                onClick={() => setBulkDialogOpen(true)}
                data-testid="bulk-physician-btn"
              >
                <CalendarRange className="h-4 w-4 mr-2" />
                Bulk Add (Date Range)
              </Button>
              
              {/* Secondary: Single Day */}
              <Button 
                variant="outline"
                onClick={() => openSingleDayDialog(currentDate)}
                data-testid="single-day-physician-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Single Day
              </Button>
              
              {/* More Actions Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="more-actions-physician">
                    <Layers className="h-4 w-4 mr-2" />
                    More Actions
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => setCopyDuplicateDialogOpen(true)}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Day / Duplicate Week
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Use "Bulk Add" to schedule 2-3 months at once with repeat patterns. "Add Single Day" for quick individual entries.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Fairness Panel - Admin Only */}
      {isAdmin && fairnessStats && fairnessStats.stats.length > 0 && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200" data-testid="fairness-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <Lightbulb className="h-5 w-5 text-amber-600" />
              After-Hours Distribution
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {fairnessStats.stats.map((stat, index) => (
                <div 
                  key={stat.physician_id}
                  className={`p-3 rounded-lg ${index === 0 ? 'bg-amber-100 ring-2 ring-amber-400' : 'bg-white'}`}
                >
                  <p className="font-medium text-slate-900">{stat.physician_name}</p>
                  <div className="flex gap-3 mt-1 text-sm">
                    <span className="text-slate-600">Weekday: {stat.weekday_afterhours_count}</span>
                    <span className="text-slate-600">Saturday: {stat.saturday_afterhours_count}</span>
                  </div>
                  {index === 0 && (
                    <Badge className="mt-2 bg-amber-500 text-white">Suggested Next</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar Controls */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigate('prev')}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => navigate('next')}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <h2 className="text-xl font-semibold text-slate-900 ml-2">
                {viewMode === 'month' && format(currentDate, 'MMMM yyyy')}
                {viewMode === 'week' && `${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d, yyyy')}`}
                {viewMode === 'day' && format(currentDate, 'EEEE, MMMM d, yyyy')}
              </h2>
            </div>
            <div className="flex gap-2">
              <Button 
                variant={viewMode === 'month' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setViewMode('month')}
              >
                Month
              </Button>
              <Button 
                variant={viewMode === 'week' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setViewMode('week')}
              >
                Week
              </Button>
              <Button 
                variant={viewMode === 'day' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setViewMode('day')}
              >
                Day
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentDate(new Date())}
              >
                Today
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : viewMode === 'month' ? (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-7 bg-slate-50">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-slate-600 border-b border-slate-200">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {monthDays.map((day) => {
                  const daySchedules = getSchedulesForDate(day);
                  const isCurrentMonth = isSameMonth(day, currentDate);
                  const isToday = isSameDay(day, new Date());
                  
                  return (
                    <div
                      key={day.toISOString()}
                      className={`min-h-[100px] p-1 border-b border-r border-slate-200 
                        ${!isCurrentMonth ? 'bg-slate-50 text-slate-400' : 'bg-white'}
                        ${isToday ? 'bg-teal-50' : ''}
                        hover:bg-slate-50 cursor-pointer`}
                      onClick={() => isAdmin && openSingleDayDialog(day)}
                    >
                      <p className={`text-sm font-medium mb-1 ${isToday ? 'text-teal-600' : ''}`}>
                        {format(day, 'd')}
                      </p>
                      <div className="space-y-1">
                        {daySchedules.slice(0, 3).map(schedule => renderScheduleEvent(schedule, true))}
                        {daySchedules.length > 3 && (
                          <p className="text-xs text-slate-500">+{daySchedules.length - 3} more</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : viewMode === 'week' ? (
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                <div className="grid grid-cols-8 border-b border-slate-200">
                  <div className="p-3 text-sm font-medium text-slate-500">Time</div>
                  {weekDays.map((day) => (
                    <div 
                      key={day.toISOString()} 
                      className={`p-3 text-center border-l border-slate-200 ${isSameDay(day, new Date()) ? 'bg-teal-50' : ''}`}
                    >
                      <p className="text-sm font-medium text-slate-500">{format(day, 'EEE')}</p>
                      <p className={`text-lg font-semibold ${isSameDay(day, new Date()) ? 'text-teal-600' : 'text-slate-900'}`}>
                        {format(day, 'd')}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="relative">
                  {HOURS.map((hour) => (
                    <div key={hour} className="grid grid-cols-8 border-b border-slate-100">
                      <div className="p-2 text-xs text-slate-500 text-right pr-4">{hour}:00</div>
                      {weekDays.map((day) => {
                        const daySchedules = getSchedulesForDate(day).filter(s => {
                          const startHour = parseInt(s.start_time.split(':')[0]);
                          return startHour === hour;
                        });
                        return (
                          <div 
                            key={day.toISOString()} 
                            className="border-l border-slate-200 min-h-[60px] p-1 hover:bg-slate-50 cursor-pointer"
                            onClick={() => isAdmin && openSingleDayDialog(day)}
                          >
                            {daySchedules.map(schedule => renderScheduleEvent(schedule))}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {HOURS.map((hour) => {
                const hourSchedules = getSchedulesForDate(currentDate).filter(s => {
                  const startHour = parseInt(s.start_time.split(':')[0]);
                  return startHour === hour;
                });
                return (
                  <div 
                    key={hour} 
                    className="flex border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => isAdmin && openSingleDayDialog(currentDate)}
                  >
                    <div className="w-20 p-3 text-sm text-slate-500 text-right">{hour}:00</div>
                    <div className="flex-1 p-2 min-h-[60px]">
                      {hourSchedules.map(schedule => renderScheduleEvent(schedule))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-50 border-l-4 border-blue-500" />
          <span className="text-sm text-slate-600">Standard (9am-5pm)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-amber-50 border-l-4 border-amber-500" />
          <span className="text-sm text-slate-600">After-Hours</span>
        </div>
      </div>

      {/* Single Day Dialog */}
      <Dialog open={singleDayDialogOpen} onOpenChange={setSingleDayDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Physician Schedule</DialogTitle>
            <DialogDescription>
              Schedule a physician for a specific date and time
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateSchedule}>
            <div className="space-y-4 py-4">
              {conflictWarning && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <p className="text-sm text-amber-700">{conflictWarning}</p>
                </div>
              )}
              
              {/* Date Selection - Editable */}
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input 
                  type="date" 
                  value={formData.date} 
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="physician-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Physician *</Label>
                <Select 
                  value={formData.assigned_to} 
                  onValueChange={(value) => {
                    setFormData({ ...formData, assigned_to: value });
                    const conflicts = checkConflicts(value, formData.date);
                    if (conflicts.length > 0) {
                      setConflictWarning(`This physician already has ${conflicts.length} schedule(s) on this day.`);
                    } else {
                      setConflictWarning(null);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select physician" />
                  </SelectTrigger>
                  <SelectContent>
                    {physicians.map((physician) => (
                      <SelectItem key={physician.id} value={physician.id}>
                        Dr. {physician.last_name}
                        {fairnessStats?.suggested_next === physician.id && ' (Suggested for after-hours)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Quick Select Presets */}
              <div className="space-y-2">
                <Label>Quick Presets (Optional)</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant={formData.event_type === 'PHYSICIAN_CUSTOM' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFormData({ ...formData, event_type: 'PHYSICIAN_CUSTOM' })}
                  >
                    Custom Hours
                  </Button>
                  <Button
                    type="button"
                    variant={formData.event_type === 'PHYSICIAN_AFTERHOURS_WEEKDAY' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleEventTypeChange('PHYSICIAN_AFTERHOURS_WEEKDAY')}
                    className={formData.event_type === 'PHYSICIAN_AFTERHOURS_WEEKDAY' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                  >
                    After-Hours Weekday (5pm-7:30pm)
                  </Button>
                  <Button
                    type="button"
                    variant={formData.event_type === 'PHYSICIAN_AFTERHOURS_SATURDAY' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleEventTypeChange('PHYSICIAN_AFTERHOURS_SATURDAY')}
                    className={formData.event_type === 'PHYSICIAN_AFTERHOURS_SATURDAY' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                  >
                    After-Hours Saturday (8:30am-12:30pm)
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time *</Label>
                  <Input 
                    type="time" 
                    value={formData.start_time} 
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value, event_type: 'PHYSICIAN_CUSTOM' })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time *</Label>
                  <Input 
                    type="time" 
                    value={formData.end_time} 
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value, event_type: 'PHYSICIAN_CUSTOM' })} 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Add any notes..." />
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Tip:</strong> For after-hours, select a preset above. For regular schedules, enter custom times. Need to schedule multiple dates? Use "Bulk Add" above.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSingleDayDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={formLoading || !formData.assigned_to}>
                {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Create Schedule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Schedule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateSchedule}>
            <div className="space-y-4 py-4">
              {conflictWarning && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <p className="text-sm text-amber-700">{conflictWarning}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Physician</Label>
                <Select value={formData.assigned_to} onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select physician" />
                  </SelectTrigger>
                  <SelectContent>
                    {physicians.map((physician) => (
                      <SelectItem key={physician.id} value={physician.id}>Dr. {physician.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input type="time" value={formData.start_time} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input type="time" value={formData.end_time} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={formLoading}>
                {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schedule</AlertDialogTitle>
            <AlertDialogDescription>Are you sure? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSchedule} className="bg-red-600 hover:bg-red-700">
              {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Physician Dialog */}
      <BulkPhysicianDialog
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        onSuccess={fetchData}
        currentDate={currentDate}
        physicians={physicians}
        fairnessStats={fairnessStats}
      />

      {/* Copy/Duplicate Dialog */}
      <CopyDuplicateDialog
        open={copyDuplicateDialogOpen}
        onOpenChange={setCopyDuplicateDialogOpen}
        onSuccess={fetchData}
        currentDate={currentDate}
        scheduleTypeFilter="PHYSICIAN"
      />
    </div>
  );
}
